import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { campaignsApi } from "../api/campaigns";
import { useTaskHistory, useTaskQueue } from "../api/tasks";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { QueryBoundary } from "../components/QueryBoundary";
import { StatTile } from "../components/StatTile";

const DAY_MS = 86_400_000;

export function DashboardPage() {
  const [nowMs] = useState(() => Date.now());
  const queue = useTaskQueue();
  const history = useTaskHistory();
  const activeCampaigns = useQuery({
    queryKey: ["campaigns", "active"],
    queryFn: () => campaignsApi.list({ status: "active", limit: 50 }),
  });

  return (
    <div className="page">
      <PageHeader
        title="Dashboard"
        description="Your open tasks and the campaigns in flight."
      />

      <section
        className="grid gap-4 mb-10"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
      >
        <QueryBoundary query={queue}>
          {(q) => {
            const oldest = q.items.reduce<string | null>(
              (min, t) => (t.created_at && (!min || t.created_at < min) ? t.created_at : min),
              null,
            );
            const oldestDays = oldest ? Math.floor((nowMs - new Date(oldest).getTime()) / DAY_MS) : null;
            const doneWeek = (history.data ?? []).filter(
              (t) =>
                t.data.status === "completed" &&
                nowMs - new Date(t.data.closure?.at ?? t.updated_at ?? 0).getTime() < 7 * DAY_MS,
            ).length;
            return (
              <>
                <StatTile
                  label="To do"
                  figure={<Link to="/tasks" className="link">{q.count}</Link>}
                  delta={`${q.people_count} people · ${q.team_count} team`}
                  deltaTone={q.count ? "warn" : null}
                />
                <StatTile
                  label="Oldest open"
                  figure={oldestDays === null ? "–" : `${oldestDays} d`}
                  delta="since it was filed"
                  deltaTone={oldestDays !== null && oldestDays > 14 ? "warn" : null}
                />
                <StatTile label="Done" figure={history.data ? doneWeek : "…"} delta="last 7 days" />
              </>
            );
          }}
        </QueryBoundary>
      </section>

      <section className="panel panel--flush">
        <div className="panel-header">
          <h2>Active campaigns</h2>
          <span className="text-xs text-ink-low">ordered by recency</span>
        </div>

        <QueryBoundary
          query={activeCampaigns}
          empty={
            <div className="empty-state">
              <div className="title">No active campaigns</div>
              <p className="hint">The seats run campaigns – the Campaigns page shows each one.</p>
            </div>
          }
          isEmpty={(c) => c.length === 0}
        >
          {(campaigns) => (
            <ul className="divide-y" style={{ borderColor: "var(--c-rule)" }}>
              {campaigns.map((campaign) => {
                const d = campaign.data;
                return (
                  <li key={campaign.id}>
                    <Link
                      to={`/campaigns/${d.slug}`}
                      className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-surface-tint"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-semibold text-ink">
                            {d.name}
                          </span>
                          <Pill value={d.status} />
                        </div>
                        <div className="mt-0.5 text-xs text-ink-low">
                          {d.slug} · {d.campaign_type}
                        </div>
                      </div>
                      <div className="text-right text-sm space-y-0.5 shrink-0 text-ink-low">
                        <div>
                          <span className="tnum text-ink font-medium">
                            {d.stats?.active ?? 0}
                          </span>{" "}
                          active
                        </div>
                        <div>
                          <span className="tnum text-ink font-medium">
                            {d.stats?.completed ?? 0}
                          </span>{" "}
                          done
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </QueryBoundary>
      </section>
    </div>
  );
}
